import React, { useState } from 'react';
import { Search, Filter, Calendar, Users, Target, X } from 'lucide-react';

const LeaderboardFilters = ({ filters, onFilterChange, questionnaireId, showAccuracyFilters = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleFilterChange = (key, value) => {
    onFilterChange({ [key]: value });
  };

  const clearFilters = () => {
    onFilterChange({
      searchTerm: '',
      dateRange: 'all',
      userType: 'all',
      accuracyRange: 'all',
      minAttempts: '',
      maxAttempts: ''
    });
  };

  const hasActiveFilters = () => {
    return filters.searchTerm || 
           filters.dateRange !== 'all' || 
           filters.userType !== 'all' ||
           filters.accuracyRange !== 'all' ||
           filters.minAttempts ||
           filters.maxAttempts;
  };

  return (
    <div className="mb-6">
      {/* Filter Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filtros</span>
          {hasActiveFilters() && (
            <span className="px-2 py-1 text-xs font-medium bg-green-aqua text-white rounded-full">
              Activos
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasActiveFilters() && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="w-3 h-3" />
              Limpiar
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {isExpanded ? 'Ocultar' : 'Mostrar'} filtros
          </button>
        </div>
      </div>

      {/* Expanded Filters */}
      {isExpanded && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
          {/* Search */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar usuario
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nombre o email..."
                  value={filters.searchTerm || ''}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-aqua focus:border-green-aqua"
                />
              </div>
            </div>
          </div>

          {/* Filter Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="inline w-4 h-4 mr-1" />
                Rango de fechas
              </label>
              <select
                value={filters.dateRange || 'all'}
                onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-aqua focus:border-green-aqua"
              >
                <option value="all">Todas las fechas</option>
                <option value="today">Hoy</option>
                <option value="week">Esta semana</option>
                <option value="month">Este mes</option>
                <option value="quarter">Este trimestre</option>
                <option value="year">Este año</option>
              </select>
            </div>

            {/* User Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Users className="inline w-4 h-4 mr-1" />
                Tipo de usuario
              </label>
              <select
                value={filters.userType || 'all'}
                onChange={(e) => handleFilterChange('userType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-aqua focus:border-green-aqua"
              >
                <option value="all">Todos los usuarios</option>
                <option value="students">Estudiantes</option>
                <option value="teachers">Profesores</option>
                <option value="admins">Administradores</option>
              </select>
            </div>

            {/* Accuracy Range (only for accuracy leaderboard) */}
            {showAccuracyFilters && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Target className="inline w-4 h-4 mr-1" />
                  Rango de precisión
                </label>
                <select
                  value={filters.accuracyRange || 'all'}
                  onChange={(e) => handleFilterChange('accuracyRange', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-aqua focus:border-green-aqua"
                >
                  <option value="all">Toda la precisión</option>
                  <option value="excellent">Excelente (90%+)</option>
                  <option value="very-good">Muy bueno (80-89%)</option>
                  <option value="good">Bueno (70-79%)</option>
                  <option value="fair">Regular (60-69%)</option>
                  <option value="poor">Necesita mejorar (&lt;60%)</option>
                </select>
              </div>
            )}
          </div>

          {/* Additional Filters for Accuracy Leaderboard */}
          {showAccuracyFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Intentos mínimos
                </label>
                <input
                  type="number"
                  placeholder="Ej: 3"
                  value={filters.minAttempts || ''}
                  onChange={(e) => handleFilterChange('minAttempts', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-aqua focus:border-green-aqua"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Intentos máximos
                </label>
                <input
                  type="number"
                  placeholder="Ej: 10"
                  value={filters.maxAttempts || ''}
                  onChange={(e) => handleFilterChange('maxAttempts', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-aqua focus:border-green-aqua"
                />
              </div>
            </div>
          )}

          {/* Active Filters Display */}
          {hasActiveFilters() && (
            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium text-gray-700">Filtros activos:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {filters.searchTerm && (
                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                    Buscar: {filters.searchTerm}
                  </span>
                )}
                {filters.dateRange !== 'all' && (
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                    Fecha: {filters.dateRange}
                  </span>
                )}
                {filters.userType !== 'all' && (
                  <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                    Usuario: {filters.userType}
                  </span>
                )}
                {showAccuracyFilters && filters.accuracyRange !== 'all' && (
                  <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                    Precisión: {filters.accuracyRange}
                  </span>
                )}
                {showAccuracyFilters && filters.minAttempts && (
                  <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-full">
                    Mín intentos: {filters.minAttempts}
                  </span>
                )}
                {showAccuracyFilters && filters.maxAttempts && (
                  <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                    Máx intentos: {filters.maxAttempts}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeaderboardFilters; 